// client/src/components/audience/AudiencePage.jsx
//
// /:tenant/audience — audience segments and targeted email / push
// (Growth Phase 5, feature_key "audience_messaging"). Lists saved segments
// and message sends, and holds the tenant's quiet hours + daily cap.
// Segments open in SegmentBuilder, drafts in MessageComposer, sent
// messages in MessageReport.
//
// API: /api/audience/:tenant (server/routes/messagingRoutes.js)

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import {
  errorText, getSettings, isNotReady, listSegments, listSends, saveSettings,
} from '../../services/audienceApi';
import { formatCount } from '../analytics/attributionFormat';
import '../analytics/PerformancePanel.css';
import '../forms/Forms.css';
import './Audience.css';

export function channelLabel(t, c) {
  return {
    email: t('audience.channel.email', 'Email'),
    push: t('audience.channel.push', 'Push'),
    both: t('audience.channel.both', 'Email + push'),
  }[c] || c;
}

export function statusLabel(t, s) {
  return {
    draft: t('audience.status.draft', 'Draft'),
    scheduled: t('audience.status.scheduled', 'Scheduled'),
    preparing: t('audience.status.preparing', 'Preparing'),
    sending: t('audience.status.sending', 'Sending'),
    sent: t('audience.status.sent', 'Sent'),
    cancelled: t('audience.status.cancelled', 'Cancelled'),
    failed: t('audience.status.failed', 'Failed'),
  }[s] || s;
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;

export default function AudiencePage({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const tenant = Number(tenantProp || params.tenant);

  const [segments, setSegments] = useState(null);
  const [sends, setSends] = useState(null);
  const [error, setError] = useState('');
  const [notReady, setNotReady] = useState(false);
  const [settings, setSettings] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const [seg, snd] = await Promise.all([listSegments(tenant), listSends(tenant)]);
      setSegments(seg.data || []);
      setSends(snd.data || []);
    } catch (err) {
      if (isNotReady(err)) setNotReady(true);
      else setError(t('audience.loadError', 'Could not load your audience.'));
    }
    try {
      const s = await getSettings(tenant);
      setSettings(s.data);
    } catch {
      setSettings(null);
    }
  }, [tenant, t]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setNotice(null);
    try {
      const res = await saveSettings(tenant, {
        quietStart: settings.quietStart === '' ? null : settings.quietStart,
        quietEnd: settings.quietEnd === '' ? null : settings.quietEnd,
        dailyCap: Number(settings.dailyCap),
      });
      setSettings(res.data);
      setNotice({ kind: 'ok', text: t('audience.settings.saved', 'Saved.') });
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('audience.settings.saveError', 'Could not save the settings.')) });
    }
  };

  const quietOff = settings && (settings.quietStart === null || settings.quietStart === '');

  const navButtons = [
    { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
    { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
  ];

  return (
    <div className="au-page">
      <DesktopActionBar buttons={navButtons} />
      <section className="surface">
        <h2>{t('audience.title', 'Audience')}</h2>
        <p className="surface-muted perf-small">
          {t('audience.intro', 'Group the people who bought tickets, checked in, entered raffles, bid in auctions or filled in your forms, then send them an email or a push notification. Unsubscribes, quiet hours and a daily limit are applied for you.')}
        </p>
      </section>

      {notReady && (
        <div className="note warn" role="status">
          {t('audience.notReady', 'Audience messaging needs a database update before it can be used.')}
        </div>
      )}
      {error && <ErrorState message={error} onRetry={load} />}

      {!notReady && !error && (
        <>
          <section className="surface">
            <div className="au-head">
              <h4>{t('audience.segments.title', 'Segments')}</h4>
              <button type="button" className="lm-btn" onClick={() => navigate(`/${tenant}/audience/segments/new`)}>
                {t('audience.segments.new', 'New segment')}
              </button>
            </div>
            {segments === null && <LoadingState />}
            {segments && segments.length === 0 && (
              <p className="surface-muted perf-small">{t('audience.segments.empty', 'No segments yet.')}</p>
            )}
            {segments && segments.length > 0 && (
              <div className="perf-table-wrap">
                <table className="perf-table">
                  <thead>
                    <tr>
                      <th>{t('audience.segments.col.name', 'Segment')}</th>
                      <th className="perf-num">{t('audience.segments.col.people', 'People')}</th>
                      <th>{t('audience.segments.col.updated', 'Last counted')}</th>
                      <th aria-label={t('audience.col.actions', 'Actions')} />
                    </tr>
                  </thead>
                  <tbody>
                    {segments.map((s) => (
                      <tr key={s.id}>
                        <td>{s.name}</td>
                        <td className="perf-num">{s.cachedCount === null ? '—' : formatCount(s.cachedCount)}</td>
                        <td>{s.lastComputedOn || t('audience.segments.notYet', 'Not yet')}</td>
                        <td>
                          <button type="button" className="lm-btn secondary" onClick={() => navigate(`/${tenant}/audience/segments/${s.id}`)}>
                            {t('audience.open', 'Open')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="surface">
            <div className="au-head">
              <h4>{t('audience.sends.title', 'Messages')}</h4>
              <button
                type="button"
                className="lm-btn"
                disabled={!segments || segments.length === 0}
                onClick={() => navigate(`/${tenant}/audience/messages/new`)}
              >
                {t('audience.sends.new', 'New message')}
              </button>
            </div>
            {segments && segments.length === 0 && (
              <p className="surface-muted perf-small">{t('audience.sends.needSegment', 'Make a segment first: every message goes to one.')}</p>
            )}
            {sends === null && <LoadingState />}
            {sends && sends.length === 0 && (
              <p className="surface-muted perf-small">{t('audience.sends.empty', 'No messages yet.')}</p>
            )}
            {sends && sends.length > 0 && (
              <div className="perf-table-wrap">
                <table className="perf-table">
                  <thead>
                    <tr>
                      <th>{t('audience.sends.col.name', 'Message')}</th>
                      <th>{t('audience.sends.col.channel', 'Channel')}</th>
                      <th>{t('audience.sends.col.segment', 'Segment')}</th>
                      <th>{t('audience.sends.col.status', 'Status')}</th>
                      <th aria-label={t('audience.col.actions', 'Actions')} />
                    </tr>
                  </thead>
                  <tbody>
                    {sends.map((s) => (
                      <tr key={s.id}>
                        <td>{s.name}</td>
                        <td>{channelLabel(t, s.channel)}</td>
                        <td>{s.segmentName || (s.triggerKey ? t('audience.sends.automatic', 'Automatic') : '—')}</td>
                        <td><span className="lm-badge">{statusLabel(t, s.status)}</span></td>
                        <td>
                          <button
                            type="button"
                            className="lm-btn secondary"
                            onClick={() => navigate(s.status === 'draft'
                              ? `/${tenant}/audience/messages/${s.id}/edit`
                              : `/${tenant}/audience/messages/${s.id}`)}
                          >
                            {s.status === 'draft' ? t('audience.edit', 'Edit') : t('audience.sends.report', 'Report')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {settings && (
            <section className="surface">
              <h4>{t('audience.settings.title', 'Sending limits')}</h4>
              <p className="surface-muted perf-small">
                {t('audience.settings.help', 'Quiet hours use your organisation time zone ({{tz}}). Messages wait until quiet hours end; nothing is dropped. The daily limit counts email and push together, per person.', { tz: settings.timezone })}
              </p>
              <form className="fo-form-grid" onSubmit={save}>
                <label className="fo-field">
                  {t('audience.settings.quietStart', 'Quiet hours start')}
                  <select
                    value={quietOff ? '' : settings.quietStart}
                    disabled={!settings.editable}
                    onChange={(e) => setSettings({ ...settings, quietStart: e.target.value === '' ? '' : Number(e.target.value) })}
                  >
                    <option value="">{t('audience.settings.off', 'Off')}</option>
                    {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                  </select>
                </label>
                <label className="fo-field">
                  {t('audience.settings.quietEnd', 'Quiet hours end')}
                  <select
                    value={settings.quietEnd === null ? '' : settings.quietEnd}
                    disabled={!settings.editable || quietOff}
                    onChange={(e) => setSettings({ ...settings, quietEnd: e.target.value === '' ? '' : Number(e.target.value) })}
                  >
                    <option value="">{t('audience.settings.off', 'Off')}</option>
                    {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                  </select>
                </label>
                <label className="fo-field">
                  {t('audience.settings.dailyCap', 'Most messages per person per day (0 = no limit)')}
                  <input
                    type="number"
                    min={0}
                    max={20}
                    value={settings.dailyCap}
                    disabled={!settings.editable}
                    onChange={(e) => setSettings({ ...settings, dailyCap: e.target.value })}
                  />
                </label>
                <div className="fo-field" style={{ justifyContent: 'flex-end' }}>
                  <button type="submit" className="lm-btn" disabled={!settings.editable}>
                    {t('audience.settings.save', 'Save limits')}
                  </button>
                </div>
              </form>
              {!settings.editable && (
                <div className="note info">{t('audience.settings.notEditable', 'These are the defaults. Changing them needs a database update.')}</div>
              )}
              {notice && <div className={`note ${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.text}</div>}
            </section>
          )}
        </>
      )}

      <AppFooter buttons={navButtons} />
    </div>
  );
}
